import type { AgentBillingAddPublishedElementsModalProps } from "@v2/types/modalTypes";

import * as React from "react";

import { SplitModal } from "@replohq/lattice/blocks/modal/SplitModal";
import { Button } from "@replohq/lattice/components/button/Button";
import { Tooltip } from "@replohq/lattice/components/tooltip/Tooltip";
import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspace } from "@v2/contexts/WorkspaceDashboardContext";
import { useCanManageWorkspaceBilling } from "@v2/features/billing/hooks/useCanManageWorkspaceBilling";
import { analytics } from "@v2/features/infra/analytics/tracking";
import { useModal } from "@v2/store/modalStore";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import { formatNumber } from "replo-utils/lib/format";
import { pluralize } from "replo-utils/lib/pluralize";

import { showAgentBillingUpdateSuccessToast } from "../agentBillingToasts";
import AddSubscriptionItemContent from "../components/AddSubscriptionItemContent";
import useAgentApplySubscriptionChangeMutation from "../hooks/useAgentApplySubscriptionChangeMutation";
import { useAgentBillingSubscription } from "../hooks/useAgentSubscription";
import { useRateCards } from "../hooks/useRateCards";

const MODAL_GRAPHIC_SRC = "/images/billing/published-items-modal-graphic.png";

const AgentBillingAddPublishedElementsModal: React.FC<
  AgentBillingAddPublishedElementsModalProps
> = () => {
  const canManageBilling = useCanManageWorkspaceBilling();
  const modal = useModal();

  const closeModal = () => {
    modal.closeModal({ type: "agentBillingAddPublishedElementsModal" });
  };

  // Viewers and project guests still need to see why publishing failed, but
  // can't buy add-ons. The billing queries 403 for guests, so they live in a
  // child that only mounts for users who can manage billing.
  if (canManageBilling === false) {
    return (
      <SplitModal
        open={true}
        onOpenChange={(open) => {
          if (!open) {
            closeModal();
          }
        }}
        media={
          <img
            src={MODAL_GRAPHIC_SRC}
            alt=""
            className="h-full w-full object-cover"
          />
        }
      >
        <NoBillingAccessContent onClose={closeModal} />
      </SplitModal>
    );
  }

  return <PurchasePublishedElementsModal closeModal={closeModal} />;
};

const PurchasePublishedElementsModal = ({
  closeModal,
}: {
  closeModal: () => void;
}) => {
  const workspace = useCurrentWorkspace();
  const { data: activeSubscription, currentDraft } =
    useAgentBillingSubscription();
  const { rateCards } = useRateCards();
  const { data: publishedElementsUsage } =
    workspaceRouter.getPublishedElementsUsage.useQuery(
      workspace?.id ?? skipToken,
    );

  const { startSubscriptionChange, isLoading: isLoadingSubscriptionUpdate } =
    useAgentApplySubscriptionChangeMutation();

  if (
    !workspace ||
    !activeSubscription ||
    !rateCards ||
    !currentDraft ||
    !publishedElementsUsage ||
    publishedElementsUsage.maximum === null ||
    !activeSubscription.currentPeriodEnd ||
    // (yuxin, 2026-07-14, REPL-29329): custom-contract add-ons go through support.
    activeSubscription.hasCustomPricing
  ) {
    return null;
  }

  const rateCard = rateCards.find(
    (rateCard) => rateCard.plan === activeSubscription.plan,
  );
  const publishedElementsProduct = rateCard?.addOnProducts.find((product) => {
    return product.featureType === "shopifyThemeBuilder.publishedElements";
  });

  const productDisplayName =
    publishedElementsProduct?.displayName ?? "Published Item";

  const maxAdditionalPublishedElements =
    publishedElementsProduct?.maxProductCount !== null &&
    publishedElementsProduct?.maxProductCount !== undefined
      ? publishedElementsProduct.maxProductCount -
        publishedElementsProduct.currentProductCount
      : undefined;

  const content = publishedElementsProduct ? (
    <AddSubscriptionItemContent
      currentSubscriptionDraft={currentDraft}
      product={publishedElementsProduct}
      currentCount={publishedElementsUsage.maximum}
      interval={activeSubscription.interval}
      currentPeriodEnd={activeSubscription.currentPeriodEnd}
      rateCards={rateCards}
      title="Add published items"
      maxQuantity={maxAdditionalPublishedElements}
      onConfirm={({ subscriptionView, additionalCount }) => {
        analytics.logEvent("agentBilling.addPublishedElements.submitted", {
          currentPlan: activeSubscription.plan,
          additionalCount,
        });

        startSubscriptionChange(
          {
            workspaceId: workspace.id,
            subscriptionView,
            successUrl: window.location.href,
            cancelUrl: window.location.href,
          },
          {
            onSuccess: (result) => {
              // A bank-approval change isn't applied yet; the approval notice replaces the success toast.
              if (result.confirmationKind !== "bankApproval") {
                showAgentBillingUpdateSuccessToast(
                  `Added ${formatNumber(additionalCount)} additional ${pluralize(productDisplayName, additionalCount)}`,
                  { showConfetti: true },
                );
              }
              closeModal();
            },
          },
        );
      }}
      isLoading={isLoadingSubscriptionUpdate}
    />
  ) : null;

  return (
    <SplitModal
      open={true}
      onOpenChange={(open) => {
        if (!open) {
          closeModal();
        }
      }}
      media={
        <img
          src={MODAL_GRAPHIC_SRC}
          alt=""
          className="h-full w-full object-cover"
        />
      }
    >
      {content}
    </SplitModal>
  );
};

const NoBillingAccessContent = ({ onClose }: { onClose: () => void }) => {
  return (
    <div className="flex flex-1 flex-col justify-between gap-6">
      <div className="flex flex-col gap-4">
        <div className="text-3xl font-editorial">Add published items</div>
        <p className="text-sm">
          This workspace has reached its published items limit. Only workspace
          owners and members can add published items or change this workspace's
          plan. Ask one of them to add published items or unpublish an item,
          then try publishing again.
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <Tooltip
          content={"Only workspace owners and members can add published items."}
          triggerAsChild
        >
          <span>
            <Button variant="primary" size="base" className="w-full" disabled>
              Add published items
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="secondary"
          size="base"
          className="w-full"
          onClick={onClose}
        >
          Close
        </Button>
      </div>
    </div>
  );
};

export default AgentBillingAddPublishedElementsModal;
